import { PaymentStatus } from 'types/payments'
import { Currency } from 'types/types'
import {
  FiatPayout,
  GetEditBeneficiaryPayloadProps,
  MassPayoutCryptoBatch,
  PayoutStatuses,
  PreProcessedPayouts,
} from './types'

export const getSumOfRecords = (records: PayoutStatuses | undefined | null): number => {
  if (!records) return 0
  return Object.values(records).reduce((sum, value) => sum + value, 0)
}

export const getValidRecords = (records: PayoutStatuses | undefined | null): number | undefined => {
  if (records && PaymentStatus.VALID in records) {
    return records[PaymentStatus.VALID]
  }
  return 0
}

export const getCurrencyName = (
  currency: string,
  protocol: string,
  currencies: Currency[]
): string => {
  if (!currencies?.length) return 'fallback'

  const currencyObj = currencies?.find(c => c.code === currency)

  if (!currencyObj) {
    return 'fallback'
  }

  const protocolObj = currencyObj?.protocols?.find(p => p.networkCode === protocol)
  const currencyCode = currencyObj?.code
  const protocolCode = protocolObj?.code

  if (currencyCode === protocolCode) {
    return `${currencyObj?.code?.toLowerCase()}`
  }

  return `${currencyObj?.code?.toLowerCase()}-${protocolObj?.code?.toLowerCase()}`
}

export const getNetworkByCurrencyAndProtocol = (
  currency: string,
  protocol: string,
  currencies: Currency[]
): string => {
  return currencies
    ?.find(_currency => _currency?.code === currency)
    ?.protocols?.find(_protocol => _protocol?.code === protocol)?.networkCode
}

export const getSumOfRecordsWithErrors = (batch: MassPayoutCryptoBatch): number => {
  if (!batch?.items) return 0

  return Object.values(batch.items).filter(
    item => (item?.status === 'DRAFT' || item?.status === 'ERROR') && item?.errorMessage !== null
  ).length
}

export const getSumOfFiatRecordsWithErrors = (batch: FiatPayout): number => {
  if (!batch?.preProcessedPayouts?.length) return 0

  return batch?.preProcessedPayouts.filter(
    item => !!Object.keys(item?.validationErrors || {})?.length
  ).length
}

export const getEditBeneficiaryPayload = ({
  amount,
  type,
  transferDestination,
  beneficiaryDetails,
  businessName,
  accountNumber,
  bankCode,
  bankCountry,
  paymentReference,
  recordReference,
  firstName,
  lastName,
  dateOfBirth,
}: GetEditBeneficiaryPayloadProps) => {
  return {
    data: {
      amount: amount,
      beneficiary: {
        type: type,
        transferDestination: transferDestination,
        entity: {
          firstName: firstName || null,
          lastName: lastName || null,
          dateOfBirth: dateOfBirth || null,
          name: businessName || null,
          country: beneficiaryDetails?.entity?.country || null,
        },
        bankAccount: {
          accountNumber: accountNumber,
          bankCode: bankCode,
          bankCountry: bankCountry,
        },
      },
      paymentReference: paymentReference,
    },
    reference: recordReference,
  }
}

interface IsFiatB2CPaymentProps {
  firstName: string
  lastName: string
  dateOfBirth: string
  name: string
}

export const isFiatB2CPayment = ({
  firstName,
  lastName,
  dateOfBirth,
  name,
}: IsFiatB2CPaymentProps): boolean => {
  return (!!firstName || !!lastName || !!dateOfBirth) && !name
}

export const isFiatB2CBatch = (batchRecord: PreProcessedPayouts): boolean => {
  const recordDetails = batchRecord?.details?.beneficiary

  if (!recordDetails) {
    return false
  }

  const { firstName, lastName, dateOfBirth, name } = recordDetails?.entity || {}

  return isFiatB2CPayment({ firstName, lastName, dateOfBirth, name })
}
